import { useTransitionNavigate } from '../components/PageTransition';
import { HelmetAsync } from '../components/HelmetAsync';
import { useReveal } from '../hooks/useReveal';
import { useCountUp } from '../hooks/useCountUp';
import { PrimaryButton, GhostButton, SectionLabel, Blob } from '../components/ui';
import { Hoverable } from '../components/Hoverable';
import { getFamilies } from '../data/products';
import { CLIENT_LOGOS } from '../data/images';
import { STATS, WHY_TILES, PROCESS_STEPS, INDUSTRIES } from '../data/content';
import { color, font, maxWidth } from '../styles/tokens';

const HeroDiagram = () => (
  <div
    style={{
      position: 'relative',
      background: 'rgba(255,255,255,.025)',
      border: '1px solid rgba(255,255,255,.1)',
      borderRadius: 20,
      padding: 28,
      boxShadow: '0 40px 90px -30px rgba(0,0,0,.8)',
      backdropFilter: 'blur(6px)',
    }}
  >
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
      <a href="/products/standard-honing-stone">
      <span style={{ font: `600 12.5px ${font.display}`, color: color.text, letterSpacing: '.04em' }}>STANDARD HONING STONE</span>
      </a>
      <span style={{ font: `500 11px ${font.mono}`, color: color.accent, background: color.accentBg, padding: '5px 10px', borderRadius: 7 }}>
        CBN / Diamond
      </span>
    </div>
    <div style={{ position: 'relative', aspectRatio: '16/10', borderRadius: 14, background: '#0b0f17', border: '1px solid rgba(255,255,255,.08)', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'repeating-linear-gradient(45deg,rgba(77,141,255,.32) 0 1px,transparent 1px 12px),repeating-linear-gradient(-45deg,rgba(77,141,255,.32) 0 1px,transparent 1px 12px)', backgroundSize: '17px 17px', animation: 'vt-hatch 2.6s linear infinite' }} />
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at center,transparent 26%,rgba(11,15,23,.9) 92%)' }} />
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg,transparent,rgba(77,141,255,.18),transparent)', animation: 'vt-scan 2.6s ease-in-out infinite' }} />
      {/* <div style={{ position: 'absolute', left: '8%', right: '8%', top: '43%', height: '14%', borderRadius: 6, background: 'linear-gradient(#a7c8ff,#2c5db0)', boxShadow: '0 0 26px 5px rgba(77,141,255,.55)', animation: 'vt-stroke 2.4s ease-in-out infinite' }} /> */}
      <div style={{ position: 'absolute', left: '12%', right: '12%', top: '38%', height: '26%', borderRadius: 6, background: 'linear-gradient(#c9d6ee,#aebfe6)', border: '1px solid #90a6d8' }} />
      <div style={{ position: 'absolute', left: '12%', right: '12%', top: '38%', height: '9%', background: 'repeating-linear-gradient(90deg,#5b7fd0 0 3px,#8aa4e0 3px 6px)', borderRadius: '6px 6px 0 0' }} />

      <span style={{ position: 'absolute', left: 14, bottom: 14, font: `500 10px ${font.mono}`, textTransform: 'uppercase', letterSpacing: '.05em', color: color.accentSoft, background: 'rgba(10,12,18,.85)', border: '1px solid rgba(77,141,255,.25)', padding: '5px 9px', borderRadius: 7 }}>
        <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: color.accent, boxShadow: '0 0 8px 1px rgba(77,141,255,.9)', marginRight: 6, verticalAlign: 'middle' }} />
        abrasive on steel blank · diagram
      </span>
    </div>
    <div style={{ marginTop: 18, padding: '15px 18px', background: 'rgba(77,141,255,.1)', border: '1px solid rgba(77,141,255,.25)', borderRadius: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ font: `500 13px ${font.body}`, color: color.textMuted }}>How to order</span>
      <span style={{ font: `700 15px ${font.mono}`, color: color.text }}>
        W × H × L × A <span style={{ fontSize: 11, color: color.accentSoft }}>(mm)</span>
      </span>
    </div>
  </div>
);

const Stat = ({ stat }) => {
  const { ref, value } = useCountUp(stat.to, stat.suf);
  return (
    <div data-reveal style={{ padding: '44px 22px', borderRight: '1px solid rgba(255,255,255,.07)' }}>
      <div style={{ font: `700 clamp(38px,5vw,58px)/1 ${font.display}`, color: color.text, letterSpacing: '-.02em' }}>
        <span ref={ref}>{value}</span>
      </div>
      <div style={{ marginTop: 12, font: `500 12px ${font.mono}`, color: color.textFaint, letterSpacing: '.03em', textTransform: 'uppercase' }}>
        {stat.label}
      </div>
    </div>
  );
};

const HexTile = ({ tile }) => (
  <div data-reveal style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
    <div style={{ width: 120, height: 132, clipPath: 'polygon(50% 0,100% 25%,100% 75%,50% 100%,0 75%,0 25%)', background: 'rgba(77,141,255,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 112, height: 122, clipPath: 'polygon(50% 0,100% 25%,100% 75%,50% 100%,0 75%,0 25%)', background: '#0d1017', display: 'flex', alignItems: 'center', justifyContent: 'center', font: `700 clamp(18px,2vw,24px) ${font.display}`, color: color.accent, textAlign: 'center', padding: 10 }}>
        {tile.k}
      </div>
    </div>
    <div style={{ marginTop: 18, font: `600 15px ${font.display}`, color: color.text }}>{tile.t}</div>
    <div style={{ marginTop: 6, font: `400 13px/1.5 ${font.body}`, color: color.textDim, maxWidth: 180 }}>{tile.d}</div>
  </div>
);

export const HomePage = () => {
  const navigate = useTransitionNavigate();
  const families = getFamilies();
  useReveal([]);

  const marquee = INDUSTRIES.concat(INDUSTRIES);
  const logos = CLIENT_LOGOS.concat(CLIENT_LOGOS);

  return (
    <div>
      <HelmetAsync
        title="V-Tech Industries — Precision Honing Solutions"
        description="Precision-engineered honing tools, industrial brushes, dies & molds and receiving gauges — manufactured in IMT Manesar since 2012."
        keywords="V-Tech Industries, honing stones, honing tools, industrial brushes, IMT Manesar, precision tooling"
        url="/"
      />

      {/* ---------------- HERO ---------------- */}
      <section style={{ position: 'relative', overflow: 'hidden', borderBottom: '1px solid rgba(255,255,255,.07)' }}>
        <Blob style={{ top: -160, right: -120, width: 560, height: 560, background: 'radial-gradient(circle,rgba(77,141,255,.5),transparent 62%)', filter: 'blur(60px)', opacity: 0.55 }} animation="vt-drift1 16s ease-in-out infinite" />
        <Blob style={{ bottom: -200, left: -140, width: 520, height: 520, background: 'radial-gradient(circle,rgba(22,102,224,.42),transparent 64%)', opacity: 0.5 }} animation="vt-drift2 20s ease-in-out infinite" />
        <svg width="620" height="620" viewBox="0 0 200 200" aria-hidden="true" style={{ position: 'absolute', top: -140, right: -120, opacity: 0.08, pointerEvents: 'none', animation: 'vt-spin 60s linear infinite', transformOrigin: 'center' }}>
          <path fill="none" stroke={color.accent} strokeWidth="4" d="M 100.0 4.0 L 120.7 22.7 L 148.0 16.9 L 156.6 43.4 L 183.1 52.0 L 177.3 79.3 L 196.0 100.0 L 177.3 120.7 L 183.1 148.0 L 156.6 156.6 L 148.0 183.1 L 120.7 177.3 L 100.0 196.0 L 79.3 177.3 L 52.0 183.1 L 43.4 156.6 L 16.9 148.0 L 22.7 120.7 L 4.0 100.0 L 22.7 79.3 L 16.9 52.0 L 43.4 43.4 L 52.0 16.9 L 79.3 22.7 Z" />
          <circle cx="100" cy="100" r="50" fill="none" stroke={color.accent} strokeWidth="4" />
        </svg>

        <div style={{ position: 'relative', maxWidth, margin: '0 auto', padding: '96px 28px 0', display: 'flex', flexWrap: 'wrap', gap: 56, alignItems: 'center' }}>
          <div style={{ flex: '1 1 500px', minWidth: 300 }}>
            <div data-reveal style={{ display: 'inline-flex', alignItems: 'center', gap: 9, background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 999, padding: '8px 15px', font: `500 11.5px ${font.mono}`, color: color.textMuted, letterSpacing: '.06em' }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: color.accent, boxShadow: '0 0 12px 2px rgba(77,141,255,.8)' }} />
              PRECISION TOOLING — EST. 2012
            </div>
            <h1 data-reveal data-delay="80ms" style={{ margin: '26px 0 0', font: `700 clamp(50px,8.4vw,104px)/0.92 ${font.display}`, letterSpacing: '-.03em', color: color.text, textWrap: 'balance' }}>
              Honing<br />
              <span style={{ color: color.accent }}>Solutions</span>
            </h1>
            <p data-reveal data-delay="160ms" style={{ margin: '22px 0 0', font: `500 clamp(17px,2.2vw,22px)/1.4 ${font.display}`, color: color.textMuted }}>
              Brushes, Dies &amp; Molds · Receiving Gauges
            </p>
            <p data-reveal data-delay="220ms" style={{ margin: '20px 0 0', maxWidth: 520, font: `400 17px/1.7 ${font.body}`, color: color.textDim }}>
              Precision-engineered industrial tools for surface finishing, dimensional accuracy and quality control — trusted across
              automotive, aerospace and hydraulics.
            </p>
            <div data-reveal data-delay="300ms" style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 34 }}>
              <PrimaryButton onClick={() => navigate('/products')}>Explore Products →</PrimaryButton>
              <GhostButton onClick={() => navigate('/contact')}>Talk to an Engineer</GhostButton>
            </div>
          </div>
          <div style={{ flex: '1 1 400px', minWidth: 300, position: 'relative' }} data-reveal data-delay="200ms">
            <HeroDiagram />
          </div>
        </div>

        {/* marquee ticker */}
        <div style={{ marginTop: 76, borderTop: '1px solid rgba(255,255,255,.07)', borderBottom: '1px solid rgba(255,255,255,.07)', overflow: 'hidden', background: 'rgba(255,255,255,.015)' }}>
          <div style={{ display: 'flex', width: 'max-content', animation: 'vt-marquee 32s linear infinite' }}>
            {marquee.map((m, i) => (
              <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 20, padding: '18px 0', font: `500 15px ${font.display}`, color: color.textFaint, letterSpacing: '.02em' }}>
                {m}
                <span style={{ color: color.accent }}>✳</span>
                <span style={{ width: 20 }} />
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- STATS ---------------- */}
      <section style={{ borderBottom: '1px solid rgba(255,255,255,.07)' }}>
        <div style={{ maxWidth, margin: '0 auto', padding: '0 28px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))' }}>
          {STATS.map((s) => (
            <Stat key={s.label} stat={s} />
          ))}
        </div>
      </section>

      {/* ---------------- TRUSTED BY ---------------- */}
      <section style={{ borderBottom: '1px solid rgba(255,255,255,.07)', overflow: 'hidden' }}>
        <div style={{ maxWidth, margin: '0 auto', padding: '44px 28px 6px', textAlign: 'center' }}>
          <div style={{ font: `500 11px ${font.mono}`, letterSpacing: '.16em', color: color.textFaint }}>[ TRUSTED BY INDUSTRY LEADERS ]</div>
        </div>
        <div style={{ overflow: 'hidden', padding: '26px 0 44px', WebkitMaskImage: 'linear-gradient(90deg,transparent,#000 12%,#000 88%,transparent)', maskImage: 'linear-gradient(90deg,transparent,#000 12%,#000 88%,transparent)' }}>
          <div style={{ display: 'flex', width: 'max-content', alignItems: 'center', gap: 80, paddingLeft: 80, animation: 'vt-marquee 28s linear infinite' }}>
            {logos.map((lg, i) => (
              <Hoverable
                key={i}
                as="img"
                src={lg.src}
                alt={lg.alt}
                style={{ height: 30, width: 'auto', objectFit: 'contain', filter: 'brightness(0) invert(1)', opacity: 0.5, transition: 'opacity .3s' }}
                hoverStyle={{ opacity: 1 }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- PRODUCT FAMILIES ---------------- */}
      <section style={{ maxWidth, margin: '0 auto', padding: '96px 28px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, marginBottom: 48 }}>
          <div data-reveal>
            <SectionLabel>[ OUR PRODUCT PORTFOLIO ]</SectionLabel>
            <h2 style={{ margin: '16px 0 0', font: `600 clamp(32px,4.4vw,52px)/1.03 ${font.display}`, color: color.text, letterSpacing: '-.02em' }}>
              A comprehensive range of<br />high-precision tools
            </h2>
          </div>
          <GhostButton onClick={() => navigate('/products')} style={{ padding: '14px 22px', font: `600 14px ${font.display}` }}>
            View all products →
          </GhostButton>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(250px,1fr))', gap: 18 }}>
          {families.map((f) => (
            <Hoverable
              key={f.name}
              as="button"
              data-reveal=""
              onClick={() => navigate('/products')}
              style={{
                position: 'relative',
                textAlign: 'left',
                background: color.panel,
                border: `1px solid ${color.panelBorder}`,
                borderRadius: 18,
                padding: 26,
                cursor: 'pointer',
                overflow: 'hidden',
                transition: 'transform .35s cubic-bezier(.16,1,.3,1),box-shadow .35s,border-color .35s,background .35s',
              }}
              hoverStyle={{ transform: 'translateY(-6px)', boxShadow: '0 30px 60px -30px rgba(77,141,255,.55)', borderColor: 'rgba(77,141,255,.5)', background: 'rgba(77,141,255,.05)' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ width: 50, height: 50, borderRadius: 14, background: color.accentBg, border: `1px solid ${color.accentBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', font: `700 16px ${font.display}`, color: color.accent }}>
                  {f.abbr}
                </div>
                <span style={{ font: `500 12px ${font.mono}`, color: color.textFainter }}>{f.idx}</span>
              </div>
              <div style={{ marginTop: 20, font: `600 19px ${font.display}`, color: color.text }}>{f.name}</div>
              <div style={{ marginTop: 9, font: `400 13.5px/1.55 ${font.body}`, color: color.textDim }}>{f.blurb}</div>
              <div style={{ marginTop: 18, font: `500 12px ${font.mono}`, color: color.accent }}>{f.count} PRODUCTS →</div>
            </Hoverable>
          ))}
        </div>
      </section>

      {/* ---------------- WHY ---------------- */}
      <section style={{ background: 'rgba(255,255,255,.015)', borderTop: '1px solid rgba(255,255,255,.07)', borderBottom: '1px solid rgba(255,255,255,.07)' }}>
        <div style={{ maxWidth, margin: '0 auto', padding: '96px 28px' }}>
          <div data-reveal style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto 56px' }}>
            <SectionLabel>[ WHY V-TECH INDUSTRIES ]</SectionLabel>
            <h2 style={{ margin: '16px 0 0', font: `600 clamp(30px,3.8vw,44px)/1.06 ${font.display}`, color: color.text, letterSpacing: '-.02em' }}>Partners in your progress</h2>
            <p style={{ margin: '18px 0 0', font: `400 16px/1.6 ${font.body}`, color: color.textDim }}>
              More than manufacturers — we deliver precision, reliability and value with every product.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 26 }}>
            {WHY_TILES.map((w) => (
              <HexTile key={w.t} tile={w} />
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- PROCESS CTA ---------------- */}
      <section style={{ maxWidth, margin: '0 auto', padding: '96px 28px', display: 'flex', flexWrap: 'wrap', gap: 52, alignItems: 'center' }}>
        <div data-reveal style={{ flex: '1 1 360px', minWidth: 280 }}>
          <SectionLabel>[ THE HONING PROCESS ]</SectionLabel>
          <h2 style={{ margin: '16px 0 0', font: `600 clamp(30px,3.8vw,44px)/1.06 ${font.display}`, color: color.text, letterSpacing: '-.02em' }}>
            Precision machining for demanding surfaces
          </h2>
          <p style={{ margin: '20px 0 0', font: `400 16px/1.7 ${font.body}`, color: color.textDim }}>
            Honing improves the geometric form, surface finish and dimensional accuracy of a workpiece — correcting shape
            distortions, achieving tight tolerances and producing a cross-hatch pattern that aids lubrication in engine cylinders,
            hydraulic bores and gear bores.
          </p>
          <PrimaryButton
            onClick={() => navigate('/honing')}
            style={{ marginTop: 30, background: color.text, color: color.bg, padding: '16px 26px', font: `600 15px ${font.display}`, boxShadow: 'none' }}
          >
            See honing solutions →
          </PrimaryButton>
        </div>
        <div style={{ flex: '1 1 360px', minWidth: 280, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {PROCESS_STEPS.map((p) => (
            <Hoverable
              key={p.n}
              as="div"
              data-reveal=""
              style={{ background: color.panel, border: `1px solid ${color.panelBorder}`, borderRadius: 16, padding: 22, transition: 'border-color .3s,background .3s' }}
              hoverStyle={{ borderColor: 'rgba(77,141,255,.4)', background: 'rgba(77,141,255,.04)' }}
            >
              <div style={{ font: `700 13px ${font.mono}`, color: color.accent }}>{p.n}</div>
              <div style={{ marginTop: 12, font: `600 15px ${font.display}`, color: color.text }}>{p.t}</div>
              <div style={{ marginTop: 6, font: `400 13px/1.5 ${font.body}`, color: color.textDim }}>{p.d}</div>
            </Hoverable>
          ))}
        </div>
      </section>
    </div>
  );
};
